// A brand colour, and the WCAG 2.2 arithmetic that decides whether it may be used
// (spec/03 §12 "Design tokens", decided in #463; spec/BRAND.md §4.3).
//
// A buyer supplies at most two colours. Each is an opaque sRGB hex — `#RRGGBB`, nothing else — so
// what the platform checks is exactly what a screen paints: no alpha to composite, no named colour a
// browser and a phone resolve differently, no shorthand.
//
// The contrast arithmetic is here, in the contract, rather than in `@clavian/domain`, because the
// refusal is part of the contract: a colour below AA is a validation error with the pair named
// (`theme.ts`), and the schema that refuses it runs in the server, the consoles and the build of the
// store apps alike. This package imports from no other workspace, so the arithmetic lives where the
// rule does; `@clavian/domain` imports it to derive the tones a buyer does not supply.
// `src/__tests__/brand.test.ts` checks it against `packages/config/tokens/contrast.mjs`, the copy the
// design tokens' own AA gate runs, so the two cannot disagree about a ratio.
import { z } from "../zod.js";
/** `#RRGGBB`. Either case is accepted; derived colours are written in upper case. */
export const HEX_COLOUR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
export const HexColourSchema = z
    .string()
    .regex(HEX_COLOUR_PATTERN, 'Expected an opaque sRGB colour as #RRGGBB, for example #7C3AED')
    .openapi('HexColour', { description: 'An opaque sRGB colour, `#RRGGBB`.', example: '#7C3AED' });
/** WCAG 2.2 AA for body text (1.4.3). */
export const WCAG_AA_TEXT = 4.5;
/** WCAG 2.2 AA for large text (1.4.3) and for interface parts and graphics (1.4.11). */
export const WCAG_AA_LARGE_AND_UI = 3;
/** `#RRGGBB` → [r, g, b] in 0..255. Throws on anything else: callers validate first. */
export function hexChannels(hex) {
    if (!HEX_COLOUR_PATTERN.test(hex))
        throw new RangeError(`Not a #RRGGBB colour: ${hex}`);
    return [Number.parseInt(hex.slice(1, 3), 16), Number.parseInt(hex.slice(3, 5), 16), Number.parseInt(hex.slice(5, 7), 16)];
}
/** [r, g, b] in 0..255 → `#RRGGBB`, rounding and clamping each channel. */
export function channelsHex(channels) {
    return `#${channels
        .map((channel) => Math.min(255, Math.max(0, Math.round(channel))).toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()}`;
}
const toLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
/** WCAG 2.2 relative luminance of an opaque colour, 0 (black) to 1 (white). */
export function relativeLuminance(hex) {
    const [r, g, b] = hexChannels(hex);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}
/** WCAG 2.2 contrast ratio of two opaque colours, 1 to 21. Symmetric. */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/** A ratio as a person reads it — rounded **down** to two decimals, so a reported ratio never flatters. */
export function formatRatio(ratio) {
    return `${(Math.floor(ratio * 100) / 100).toFixed(2)}:1`;
}
//# sourceMappingURL=colour.js.map